"use client";

import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Loader, ArrowRight } from "lucide-react";
function InquiryForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-8 border border-slate-200">
        <p className="font-default italic text-xl text-slate-900 leading-relaxed">
          {successMessage}
        </p>
        <Button onClick={resetForm} variant="outline" className="mt-6 rounded-lg border-slate-300 text-slate-900 bg-transparent hover:bg-slate-100 hover:text-blue-700 hover:border-blue-700 uppercase tracking-[0.08em] text-xs h-auto px-6 py-3">
          Submit Another Inquiry
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-8">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <FormField control={control} name="name" render={({
        field
      }) => <FormItem>
              <FormLabel className="font-default text-[10px] tracking-[0.22em] uppercase text-slate-500">
                Name
              </FormLabel>
              <FormControl>
                <Input placeholder="Sunil Singh" className="rounded-lg bg-transparent border-0 border-b border-slate-300 px-0 py-3 h-auto text-slate-900 placeholder:text-slate-400 focus-visible:ring-0 focus-visible:border-blue-700 focus-visible:border-b-2" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
        <FormField control={control} name="email" render={({
        field
      }) => <FormItem>
              <FormLabel className="font-default text-[10px] tracking-[0.22em] uppercase text-slate-500">
                Email
              </FormLabel>
              <FormControl>
                <Input type="email" placeholder="sunil@hotelrevbaba.com" className="rounded-lg bg-transparent border-0 border-b border-slate-300 px-0 py-3 h-auto text-slate-900 placeholder:text-slate-400 focus-visible:ring-0 focus-visible:border-blue-700 focus-visible:border-b-2" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem>
              <FormLabel className="font-default text-[10px] tracking-[0.22em] uppercase text-slate-500">
                Phone
              </FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+1 555-000-0000" className="rounded-lg bg-transparent border-0 border-b border-slate-300 px-0 py-3 h-auto text-slate-900 placeholder:text-slate-400 focus-visible:ring-0 focus-visible:border-blue-700 focus-visible:border-b-2" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="date" render={({
        field
      }) => <FormItem>
              <FormLabel className="font-default text-[10px] tracking-[0.22em] uppercase text-slate-500">
                Preferred Consultation Date
              </FormLabel>
              <FormControl>
                <Input type="date" className="rounded-lg bg-transparent border-0 border-b border-slate-300 px-0 py-3 h-auto text-slate-900 placeholder:text-slate-400 focus-visible:ring-0 focus-visible:border-blue-700 focus-visible:border-b-2" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
        <FormField control={control} name="property_type" render={({
        field
      }) => <FormItem>
              <FormLabel className="font-default text-[10px] tracking-[0.22em] uppercase text-slate-500">
                Property Type
              </FormLabel>
              <FormControl>
                <Input placeholder="e.g. Boutique Hotel" className="rounded-lg bg-transparent border-0 border-b border-slate-300 px-0 py-3 h-auto text-slate-900 placeholder:text-slate-400 focus-visible:ring-0 focus-visible:border-blue-700 focus-visible:border-b-2" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="time" render={({
      field
    }) => <FormItem>
            <FormLabel className="font-default text-[10px] tracking-[0.22em] uppercase text-slate-500">
              Preferred Time
            </FormLabel>
            <Select onValueChange={field.onChange} value={field.value}>
              <FormControl>
                <SelectTrigger className="rounded-lg bg-transparent border-0 border-b border-slate-300 px-0 py-3 h-auto text-slate-900 focus-visible:ring-0 focus-visible:border-blue-700 w-full">
                  <SelectValue placeholder="Select a time" />
                </SelectTrigger>
              </FormControl>
              <SelectContent className="rounded-lg bg-white border-slate-200 text-slate-900">
                <SelectItem value="Morning">Morning</SelectItem>
                <SelectItem value="Afternoon">Afternoon</SelectItem>
                <SelectItem value="Evening">Evening</SelectItem>
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="special_requests" render={({
      field
    }) => <FormItem>
            <FormLabel className="font-default text-[10px] tracking-[0.22em] uppercase text-slate-500">
              Revenue Challenges / Goals
            </FormLabel>
            <FormControl>
              <Textarea rows={6} placeholder="Tell us about your current revenue management challenges, OTA performance, or specific goals for your property." className="rounded-lg bg-transparent border border-slate-300 px-4 py-3 text-slate-900 placeholder:text-slate-400 focus-visible:ring-0 focus-visible:border-blue-700 font-default" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 border border-red-200 bg-red-50 rounded-lg">
          <p className="text-red-700 text-sm">{submitError}</p>
        </div>}

      <div className="pt-4">
        <Button type="submit" disabled={isSubmitting} className="rounded-lg bg-blue-700 text-white hover:bg-blue-800 uppercase tracking-[0.08em] font-default font-medium text-xs px-9 py-5 h-auto w-full md:w-auto">
          {isSubmitting ? <>
              <Loader className="animate-spin mr-2 size-4" />
              Submitting...
            </> : <>
              Book a Consultation
              <ArrowRight className="ml-2 size-4" />
            </>}
        </Button>
        <p className="mt-6 font-default text-[10px] tracking-[0.22em] uppercase text-slate-500">
          Sunil Singh reviews every inquiry personally within 24 hours.
        </p>
      </div>
    </Form>;
}
export default function Contact() {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const microCards = [{
    label: "Expertise",
    value: "Revenue Management"
  }, {
    label: "Focus",
    value: "OTA Strategy"
  }, {
    label: "Response",
    value: "Within 24 Hours"
  }];
  return <section data-section-id="1033" id="contactpricinglead" className="bg-slate-50 text-slate-900 py-24 md:py-32 lg:py-44 relative border-t border-slate-200">
      <div className="absolute top-0 left-0 right-0 h-px bg-slate-200 pointer-events-none">
        <div className="absolute top-1/2 -translate-y-1/2 left-[41.66%] w-1 h-1 rounded-lg bg-blue-700" />
      </div>

      <div className="max-w-[1440px] mx-auto px-5 md:px-8 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <div ref={leftRef} data-index={0} className={`lg:col-span-5 transition-all duration-[800ms] ease-out ${leftVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
            <div className="font-default text-[11px] tracking-[0.22em] uppercase text-blue-700 mb-12 md:mb-16">
              <span className="font-default text-base not-italic mr-2">01</span>
              <span>/ § Contact — HotelRevBaBa</span>
            </div>

            <h2 className="font-default font-bold text-slate-900 leading-[1.02] tracking-[-0.02em] text-[clamp(2.25rem,5vw,4rem)]">
              <em>Maximize Your Revenue</em>
            </h2>

            <div className="relative my-10 w-[200px] h-px bg-slate-300">
              <div className="absolute top-1/2 -translate-y-1/2 right-0 w-1 h-1 rounded-lg bg-blue-700" />
            </div>

            <p className="font-default text-[clamp(1.125rem,1.5vw,1.375rem)] leading-[1.6] text-slate-700 max-w-[420px]">
              Ready to transform your hotel's performance? Connect with Sunil Singh to discuss data-driven OTA strategies, tech stack optimization, and revenue audits tailored to your property.
            </p>

            <div className="mt-14 space-y-0">
              {microCards.map((card, i) => <div key={i} data-index={i} className="py-6 border-b border-slate-200 first:border-t">
                  <div className="font-default text-[11px] tracking-[0.22em] uppercase text-slate-500">
                    {card.label}
                  </div>
                  <div className="font-default text-lg text-slate-900 mt-2">
                    {card.value}
                  </div>
                </div>)}
            </div>
          </div>

          <div ref={rightRef} data-index={1} className={`lg:col-span-7 transition-all duration-[800ms] ease-out delay-150 ${rightVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
            <div className="relative bg-white border border-slate-200 p-6 md:p-10 lg:p-12 shadow-lg rounded-lg">
              <div className="absolute top-0 left-0 w-3 h-3 border-t border-l border-blue-700" />
              <div className="absolute top-0 right-0 w-3 h-3 border-t border-r border-blue-700" />
              <div className="absolute bottom-0 left-0 w-3 h-3 border-b border-l border-blue-700" />
              <div className="absolute bottom-0 right-0 w-3 h-3 border-b border-r border-blue-700" />

              <div className="font-default text-[11px] tracking-[0.22em] uppercase text-blue-700 mb-10">
                Consultation Request
              </div>

              <FormProvider formKey="revenue_journey_form_contact" formId="revenue_journey_form_contact" sectionName="contactpricinglead" defaultValues={{
              name: "",
              email: "",
              phone: "",
              date: "",
              property_type: "",
              time: "",
              special_requests: ""
            }} validationRules={{
              name: {
                required: true,
                minLength: 2,
                maxLength: 100
              },
              email: {
                required: true,
                pattern: "email"
              },
              phone: {
                required: true,
                pattern: "phone"
              },
              date: {
                required: true
              },
              property_type: {
                required: true
              },
              time: {
                required: true
              },
              special_requests: {
                required: false
              }
            }} submitText="Book a Consultation" successMessage="Thank you for reaching out. Sunil Singh will review your request and contact you within 24 hours." errorMessage="We were unable to submit your request. Please try again or email us directly.">
                <InquiryForm />
              </FormProvider>
            </div>
          </div>
        </div>
      </div>
    </section>;
}